import { createElement, type ReactNode } from 'react';
import {
  IconBell,
  IconCampaigns,
  IconCatalog,
  IconDashboard,
  IconInsights,
  IconOnboarding,
  IconOrders,
  IconPayouts,
  IconStatements,
} from '../components/icons.js';

/**
 * The surface registry (clarity-navigation-scope design §4, Req 4.1) — THE single
 * source of truth for the six workspace surfaces, shared by the router
 * (`AppRoutes`) and the rail (`Shell`). Declaring id, i18n label key, icon, and
 * scope capability once means adding or re-capability-ing a surface can't drift
 * between where it is routed and where it is shown.
 *
 * This module is a LEAF: it imports only presentational icons + React. It must
 * never import `scopeContext` (which imports this), the router, or any feature —
 * that keeps the capability lookup importable from both the router and the rail
 * without an import cycle.
 */

/** The workspace surfaces, in rail order. */
export type SurfaceId =
  | 'dashboard'
  | 'orders'
  | 'statements'
  | 'payouts'
  | 'campaigns'
  | 'insights'
  | 'notifications'
  | 'catalog'
  | 'onboarding';

/**
 * A surface's scope capability (design §4, §8 truth table):
 * - `flexible`    — valid at All, Brand, and Branch scope (dashboard/orders/
 *   statements/payouts); aggregates across the in-scope branches.
 * - `branch-only` — meaningful ONLY at a single branch (catalog/onboarding); an
 *   All/Brand URL narrows to one branch or prompts pick-a-branch (Req 4.2/4.5).
 */
export type SurfaceCapability = 'flexible' | 'branch-only';

export interface Surface {
  /** The URL surface segment + stable key. */
  id: SurfaceId;
  /** The i18n dictionary key for the rail label (see `lib/i18n`). */
  labelKey: string;
  /** The drawn rail icon (currentColor line icon from `components/icons`). */
  icon: ReactNode;
  /** Whether the surface works aggregated (flexible) or only per branch. */
  capability: SurfaceCapability;
}

/**
 * The ordered registry. `icon` is built with `createElement` (not JSX) so this
 * stays a plain `.ts` leaf — the rail passes the shared `.nav-ico` class exactly
 * as the existing `RailLink` icons do.
 */
export const SURFACES: readonly Surface[] = [
  {
    id: 'dashboard',
    labelKey: 'nav.dashboard',
    icon: createElement(IconDashboard, { className: 'nav-ico' }),
    capability: 'flexible',
  },
  {
    id: 'orders',
    labelKey: 'nav.orders',
    icon: createElement(IconOrders, { className: 'nav-ico' }),
    capability: 'flexible',
  },
  {
    id: 'statements',
    labelKey: 'nav.statements',
    icon: createElement(IconStatements, { className: 'nav-ico' }),
    capability: 'flexible',
  },
  {
    id: 'payouts',
    labelKey: 'nav.payouts',
    icon: createElement(IconPayouts, { className: 'nav-ico' }),
    capability: 'flexible',
  },
  {
    id: 'campaigns',
    labelKey: 'nav.campaigns',
    icon: createElement(IconCampaigns, { className: 'nav-ico' }),
    capability: 'flexible',
  },
  {
    id: 'insights',
    labelKey: 'nav.insights',
    icon: createElement(IconInsights, { className: 'nav-ico' }),
    capability: 'flexible',
  },
  {
    id: 'notifications',
    labelKey: 'nav.notifications',
    icon: createElement(IconBell, { className: 'nav-ico' }),
    capability: 'flexible',
  },
  {
    id: 'catalog',
    labelKey: 'nav.catalog',
    icon: createElement(IconCatalog, { className: 'nav-ico' }),
    capability: 'branch-only',
  },
  {
    id: 'onboarding',
    labelKey: 'nav.onboarding',
    icon: createElement(IconOnboarding, { className: 'nav-ico' }),
    capability: 'branch-only',
  },
] as const;

/**
 * Capability lookup by surface id — derived from {@link SURFACES} so there is
 * exactly ONE definition. `scopeContext` imports this (not a hand-kept copy) for
 * `surfaceAvailable` / `goToScope`'s surface-preservation rule.
 */
export const SURFACE_CAPABILITY: Record<SurfaceId, SurfaceCapability> =
  Object.fromEntries(SURFACES.map((s) => [s.id, s.capability])) as Record<
    SurfaceId,
    SurfaceCapability
  >;

/** The surface registry keyed by id, for quick lookup by router + rail. */
export const SURFACE_BY_ID: Record<SurfaceId, Surface> = Object.fromEntries(
  SURFACES.map((s) => [s.id, s]),
) as Record<SurfaceId, Surface>;
